import { Button, Checkbox, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, TextField, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContactName } from '../ContactSelector/ContactSelector';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import PortModel from '../PortSelector/models/PortModel';
import PortMultipleSelector from '../PortSelector/PortMultipleSelector';
import UpdatedDateSelector from './components/UpdatedDateSelector';
import TagsFilter from '../TagFilter/TagsFilter';
import { QueryItem } from './models/TonnageQueryModel';
import { tonnageInputSelector } from './reducers/TonnageInputReducer';
import { setOwnerNames, setTonnageContactNames, setTonnageContactSearchKeyword, setVesselNames, tonnageSearchSelector, setOpenPorts, setUpdatedDate, setDwtFrom, setDwtTo, setDateFrom, setDateTo, setAgeMax, setDecks, setLoaMin, setLoaMax, setDraftMax, setBeamMax, setGearMin, setCapacityMin, setGrabs, setTonnageSearchResults, setTriggerTonnageSearch, setSearchResultTotalRecord, setClasses, setOpenAreas, resetTonnageSearch, setCountries, SearchType, setSearchType, setTriggerFocusTonnageName, setGearLess, setHeadOwnerNames } from './reducers/TonnageSearchReducer';
import DwtSelector from './components/DwtSelector';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import TonnageApi, { SearchTonnageParam } from './api/TonnageApi';
import BaseResponse from '../../app/BaseResponse';
import TonnageModel from './models/TonnageModel';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import SavedSearch, { ComponentType } from '../SavedSearch/SavedSearch';
import Swal from 'sweetalert2';
import SavedSearchApi, { SaveSavedSearchParam } from '../SavedSearch/api/SavedSearchApi';
import SavedSearchModel from '../SavedSearch/models/SavedSearchModel';
import { addSavedSearch, savedSearchSelector } from '../SavedSearch/reducers/SavedSearchReducer';
import AreaModel from '../PortSelector/models/AreaModel';
import { CountryModel } from '../Contact/models/ContactStaticResourcesModel';
import { contactInputSelector } from '../Contact/reducers/ContactInputReducer';
import NumberFormatCustom from '../NumberFormatCustom/NumberFormatCustom';

const TonnageSearch: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const tonnageSearchData = useSelector(tonnageSearchSelector);
    const tonnageViewData = useSelector(tonnageInputSelector);
    const savedSearchData = useSelector(savedSearchSelector);
    const contactInputViewData = useSelector(contactInputSelector);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (tonnageSearchData.tonnageContactSearchKeyword && tonnageSearchData.tonnageContactSearchKeyword.length > 0) {
                searchContactName(tonnageSearchData.tonnageContactSearchKeyword, (contacts) => {
                    if (contacts && contacts.length > 0) {
                        dispatch(setTonnageContactNames(contacts));
                    } else {
                        dispatch(setTonnageContactNames([]));
                    }
                })
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [tonnageSearchData.tonnageContactSearchKeyword])

    const portsToQueryItems = (ports: PortModel[]) => {
        return _.map(ports, (port) => {
            return {
                id: port.id.toString(),
                value: port.name,
                positive: port.positive
            } as QueryItem
        })
    }

    const queryItemsToPorts = (queryItems: QueryItem[]) => {
        return _.map(queryItems, (queryItem) => {
            return {
                id: parseInt(queryItem.id),
                name: queryItem.value,
                positive: queryItem.positive
            } as PortModel
        })
    }

    const areasToQueryItems = (areas: AreaModel[]) => {
        return _.map(areas, (area) => {
            return {
                id: area.name,
                value: area.name,
                positive: area.positive
            } as QueryItem
        })
    }

    const queryItemsToAeras = (queryItems: QueryItem[]) => {
        return _.map(queryItems, (queryItem) => {
            return {
                name: queryItem.value,
                positive: queryItem.positive
            } as AreaModel
        })
    }

    const countryToQueryItems = (countries: CountryModel[]) => {
        return _.map(countries, (country) => {
            return {
                id: country.code,
                value: country.code,
                positive: true
            } as QueryItem
        })
    }

    const saveSavedSearch = () => {
        Swal.fire({
            title: 'Provide Saved Search Name',
            input: 'text',
            inputAttributes: {
                autocapitalize: 'off'
            },
            showCancelButton: true,
            confirmButtonText: 'Sumbit',
            showLoaderOnConfirm: true,
            allowOutsideClick: false,
            confirmButtonColor: '#1976d2',
            preConfirm: (name) => {
                if (!name || name.length === 0) {
                    Swal.showValidationMessage("Name ")
                }
            },
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));

                SavedSearchApi.Save({
                    name: result.value ? result.value : "",
                    component: ComponentType.Tonnage,
                    queryJson: JSON.stringify(tonnageSearchData.tonnageQuery)
                } as SaveSavedSearchParam)
                    .then((response: BaseResponse<SavedSearchModel>) => {
                        if (response.success) {
                            dispatch(addSavedSearch(response.data));
                            Utility.Alert.Success("Save Successful");
                        } else {
                            Utility.Alert.Error(response.message);
                        }
                    })
                    .catch((error) => {
                        Utility.Alert.Error(error);
                    })
                    .finally(() => {
                        dispatch(showLoading(false));
                    });
            }
        })
    }

    useEffect(() => {
        if (tonnageSearchData.triggerSearch === 0) {
            return;
        }

        dispatch(showLoading(true));

        TonnageApi.Search({
            query: tonnageSearchData.tonnageQuery
        } as SearchTonnageParam)
            .then((response: BaseResponse<TonnageModel[]>) => {
                if (response.success) {
                    dispatch(setTonnageSearchResults(response.data));
                    dispatch(setSearchResultTotalRecord(response.totalRecord));
                    dispatch(setSearchType(SearchType.Search));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    }, [tonnageSearchData.triggerSearch])

    const getTonnageSearchClass = () => {
        let className = "nice-scroll ";
        if (savedSearchData.expanded) {
            className += "tonnage-search-wrapper";
        } else {
            className += "tonnage-search-wrapper-saved-search-hide";
        }

        return className;
    }

    return (
        <>
            <SavedSearch
                component={ComponentType.Tonnage}
            >
            </SavedSearch>
            <CustomAccordion
                title="Search"
                defaultOpen={true}
                contentClassName={getTonnageSearchClass()}>
                <div className='tonnage-search-body'>
                    <Grid container>
                        <Grid item xs={8}>
                            <Grid container>
                                <Grid xs={8}>
                                    <Tooltip title="Alt + W">
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            className="tonnage-search-save-button"
                                            onClick={() => {
                                                dispatch(setTriggerTonnageSearch((new Date()).getTime()));
                                            }}
                                        >
                                            RUN SEARCH (w)
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid xs={4}>
                                    <Tooltip title="Reset (Alt + Q)">
                                        <Button
                                            className="clear-input-button"
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                dispatch(resetTonnageSearch());
                                                dispatch(setTriggerFocusTonnageName(new Date().getTime()));
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>

                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => {
                                    saveSavedSearch();
                                }}
                            >
                                Save Search
                            </Button>
                        </Grid>
                    </Grid>
                    <TagsFilter
                        data={_.map(tonnageViewData.tonnageNames, (item) => {
                            return {
                                id: item.id,
                                value: item.vesselName,
                                positive: true
                            } as QueryItem
                        })}
                        label="Vessel Name"
                        className="tonnage-search-item"
                        value={tonnageSearchData.tonnageQuery.vesselNames}
                        useFreeText={true}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setVesselNames(value))
                        }}
                        triggerFocusInput={tonnageSearchData.triggerFocusTonnageName}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(tonnageSearchData.tonnageContactNames, (item) => {
                            return {
                                id: item.id,
                                value: item.companyName,
                                positive: true
                            } as QueryItem
                        })}
                        label="Owners Name"
                        onInputChange={(text: string) => {
                            dispatch(setTonnageContactSearchKeyword(text));
                        }}
                        className="tonnage-search-item"
                        useFreeText={true}
                        value={tonnageSearchData.tonnageQuery.ownerNames == null ? [] : tonnageSearchData.tonnageQuery.ownerNames}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setOwnerNames(value))
                        }}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(tonnageSearchData.tonnageContactNames, (item) => {
                            return {
                                id: item.id,
                                value: item.companyName,
                                positive: true
                            } as QueryItem
                        })}
                        label="Head Owners Name"
                        onInputChange={(text: string) => {
                            dispatch(setTonnageContactSearchKeyword(text));
                        }}
                        className="tonnage-search-item"
                        useFreeText={true}
                        value={tonnageSearchData.tonnageQuery.headOwnerNames == null ? [] : tonnageSearchData.tonnageQuery.headOwnerNames}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setHeadOwnerNames(value))
                        }}
                    ></TagsFilter>
                    <PortMultipleSelector
                        onPortChange={(value: PortModel[]) => {
                            if (value) {
                                dispatch(setOpenPorts(portsToQueryItems(value)));
                            }
                        }}
                        onAreaChange={(area: AreaModel[]) => {
                            if (area) {
                                dispatch(setOpenAreas(areasToQueryItems(area)));
                            }
                        }}
                        useFreeText={true}
                        portLabel='Port Open'
                        areaLabel='Area'
                        portValues={queryItemsToPorts(tonnageSearchData.tonnageQuery.openPorts)}
                        areaValues={queryItemsToAeras(tonnageSearchData.tonnageQuery.openAreas)}
                        className="tonnage-search-item"
                        useAreaBookmark={true}
                    >
                    </PortMultipleSelector>
                    <TagsFilter
                        data={countryToQueryItems(contactInputViewData.staticResources.countries)}
                        label="Country"
                        className="tonnage-search-item"
                        value={tonnageSearchData.tonnageQuery.countries}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCountries(value))
                        }}
                    ></TagsFilter>
                    <UpdatedDateSelector
                        value={tonnageSearchData.tonnageQuery.updatedDate}
                        onChange={(value: number) => {
                            dispatch(setUpdatedDate(value));
                        }}
                        className="tonnage-search-item"
                    >
                    </UpdatedDateSelector>
                    <DwtSelector
                        dwtFromValue={tonnageSearchData.tonnageQuery.dwtFrom}
                        dwtToValue={tonnageSearchData.tonnageQuery.dwtTo}
                        className="tonnage-search-item"
                        dwtFromLabel='Dwt From(T)'
                        dwtToLabel='Dwt To(T)'
                        useSizeSelector={true}
                        onDwtFromChange={(value: number) => {
                            dispatch(setDwtFrom(value));
                        }}
                        onDwtToChange={(value: number) => {
                            dispatch(setDwtTo(value));
                        }}
                    >
                    </DwtSelector>
                    <StartEndDatepicker
                        startDateLabel="Start date"
                        endDateLabel="End date"
                        className="tonnage-search-item"
                        useQuickEndDateInput={true}
                        onStartDateChange={(date: string) => {
                            dispatch(setDateFrom(date));
                        }}
                        onEndDateChange={(date: string) => {
                            dispatch(setDateTo(date));
                        }}
                        startDateValue={tonnageSearchData.tonnageQuery.dateFrom}
                        endDateValue={tonnageSearchData.tonnageQuery.dateTo}
                    >
                    </StartEndDatepicker>
                    <TagsFilter
                        data={[
                            {
                                id: "",
                                value: "IACS",
                                positive: true
                            } as QueryItem,
                            {
                                id: "",
                                value: "NON-IACS",
                                positive: true
                            } as QueryItem,
                        ]}
                        label="Class"
                        className="tonnage-search-item"
                        value={tonnageSearchData.tonnageQuery.classes}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setClasses(value))
                        }}
                    ></TagsFilter>
                    <TagsFilter
                        data={[
                            {
                                id: "Single",
                                value: "Single",
                                positive: true
                            } as QueryItem,
                            {
                                id: "Tween",
                                value: "Tween",
                                positive: true
                            } as QueryItem,
                            {
                                id: "Flush Tween",
                                value: "Flush Tween",
                                positive: true
                            } as QueryItem,
                        ]}
                        label="Decks"
                        className="tonnage-search-item"
                        value={tonnageSearchData.tonnageQuery.decks}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setDecks(value))
                        }}
                    ></TagsFilter>
                    <TextField
                        fullWidth
                        className="tonnage-search-item"
                        label="Age Max"
                        InputProps={{
                            inputComponent: NumberFormatCustom as any,
                        }}
                        value={tonnageSearchData.tonnageQuery.ageMax ? tonnageSearchData.tonnageQuery.ageMax : ""}
                        onChange={(event) => {
                            let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                            dispatch(setAgeMax(value));
                        }}
                    >
                    </TextField>
                    <Grid container>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                className="tonnage-search-item"
                                label="Loa Min"
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                value={tonnageSearchData.tonnageQuery.loaMin ? tonnageSearchData.tonnageQuery.loaMin : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                    dispatch(setLoaMin(value));
                                }}
                            >
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                className="tonnage-search-item"
                                label="Loa Max"
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                value={tonnageSearchData.tonnageQuery.loaMax ? tonnageSearchData.tonnageQuery.loaMax : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                    dispatch(setLoaMax(value));
                                }}
                            >
                            </TextField>
                        </Grid>
                    </Grid>
                    <TextField
                        fullWidth
                        className="tonnage-search-item"
                        label="Beam Max"
                        InputProps={{
                            inputComponent: NumberFormatCustom as any,
                        }}
                        value={tonnageSearchData.tonnageQuery.beamMax ? tonnageSearchData.tonnageQuery.beamMax : ""}
                        onChange={(event) => {
                            let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                            dispatch(setBeamMax(value));
                        }}
                    >
                    </TextField>
                    <TextField
                        fullWidth
                        className="tonnage-search-item"
                        label="Draft Max"
                        InputProps={{
                            inputComponent: NumberFormatCustom as any,
                        }}
                        value={tonnageSearchData.tonnageQuery.draftMax ? tonnageSearchData.tonnageQuery.draftMax : ""}
                        onChange={(event) => {
                            let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                            dispatch(setDraftMax(value));
                        }}
                    >
                    </TextField>
                    <TextField
                        fullWidth
                        className="tonnage-search-item"
                        label="Capacity Min"
                        InputProps={{
                            inputComponent: NumberFormatCustom as any,
                        }}
                        value={tonnageSearchData.tonnageQuery.capacityMin ? tonnageSearchData.tonnageQuery.capacityMin : ""}
                        onChange={(event) => {
                            let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                            dispatch(setCapacityMin(value));
                        }}
                    >
                    </TextField>
                    <FormControl>
                        <FormLabel id="gearless">GearLess</FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby="gearless"
                            name="gearless"
                            value={tonnageSearchData.tonnageQuery.gearless === null ? "null" : tonnageSearchData.tonnageQuery.gearless ? "true" : "false"}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                dispatch(setGearLess(
                                    event.target.value === "null" ? null : event.target.value === "true" ? true : false
                                ));
                            }}
                        >
                            <FormControlLabel value="null" control={<Radio />} label="Default" />
                            <FormControlLabel value="true" control={<Radio />} label="Yes" />
                            <FormControlLabel value="false" control={<Radio />} label="No" />
                        </RadioGroup>
                    </FormControl>
                    <TextField
                        fullWidth
                        className="tonnage-search-item"
                        label="Gear Min(SWL)"
                        InputProps={{
                            inputComponent: NumberFormatCustom as any,
                        }}
                        value={tonnageSearchData.tonnageQuery.gearMin ? tonnageSearchData.tonnageQuery.gearMin : ""}
                        onChange={(event) => {
                            let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                            dispatch(setGearMin(value));
                        }}
                    >
                    </TextField>
                    <FormControl>
                        <FormLabel id="grabs">Grabs</FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby="grabs"
                            name="grabs"
                            value={tonnageSearchData.tonnageQuery.grabs === null ? "null" : tonnageSearchData.tonnageQuery.grabs ? "true" : "false"}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                dispatch(setGrabs(
                                    event.target.value === "null" ? null : event.target.value === "true" ? true : false
                                ));
                            }}
                        >
                            <FormControlLabel value="null" control={<Radio />} label="Default" />
                            <FormControlLabel value="true" control={<Radio />} label="Yes" />
                            <FormControlLabel value="false" control={<Radio />} label="No" />
                        </RadioGroup>
                    </FormControl>
                </div>
            </CustomAccordion>
        </>
    )
};

export default TonnageSearch;